"use client";

import { IconDiscordLogo, IconXLogo } from "@decibeltrade/react-icons";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { css } from "styled-system/css";
import { flex, stack } from "styled-system/patterns";

import { DISCORD_URL, X_URL } from "@/constants/urls";

import { LinkButton } from "./buttons/link-button";

export interface FullScreenErrorProps {
  heading: string;
  description: string;
}

export function FullScreenError({ heading, description }: FullScreenErrorProps) {
  const t = useTranslations("errorScreens");

  return (
    <div className={wrapperStyles}>
      <div className={mascotWrapperStyles}>
        <Image
          src="/images/mascot/error-mascot.webp"
          alt={t("errorMascotAlt")}
          fill
          className={css({ objectFit: "contain" })}
          loading="eager"
        />
      </div>
      <div className={contentWrapperStyles}>
        <h1 className={headingStyles}>{heading}</h1>
        <p className={descriptionStyles}>{description}</p>
        <div className={buttonsWrapperStyles}>
          <LinkButton
            variant="text"
            href={X_URL}
            target="_blank"
            rel="noopener noreferrer"
            TailIcon={IconXLogo}
            ariaLabel={t("viewOnX")}
          >
            {t("viewOn")}
          </LinkButton>
          <LinkButton
            variant="brand"
            href={DISCORD_URL}
            target="_blank"
            rel="noopener noreferrer"
            TailIcon={IconDiscordLogo}
          >
            {t("joinDiscord")}
          </LinkButton>
        </div>
      </div>
    </div>
  );
}

const wrapperStyles = flex({
  overflow: "hidden",
  w: "full",
  h: "full",
  p: "24",
  justify: { base: "flex-start", md: "center" },
  align: "center",
  direction: { base: "column", md: "row" },
});

const mascotWrapperStyles = css({
  position: "relative",
  h: "[319px]",
  w: "[569px]",
  mt: "[6vh]",
  mb: "[-16px]",
  mx: "[-69px]",
  md: {
    h: "[539px]",
    w: "[963px]",
    mt: "[unset]",
    mb: "[unset]",
    ml: "[-16vw]",
    mr: "[-190px]",
  },
});

const contentWrapperStyles = stack({
  gap: "32",
  maxW: "[409px]",
  zIndex: "[1]",
  md: { mt: "[-95px]" },
});

const headingStyles = css({
  color: "text.primary",
  fontSize: { base: "36", md: "40" },
  lineHeight: "[normal]",
  fontWeight: "light",
});

const descriptionStyles = css({ color: "text.secondary", textStyle: "body.xl" });

const buttonsWrapperStyles = flex({ gap: "8", align: "center" });
